import React, { useMemo, useState } from "react";
import { FaSearch, FaSort, FaSortDown, FaSortUp, FaDownload } from "react-icons/fa";
import ArticleRow from "./ArticleRow";
import "./adminDashboard.css";

function compare(a, b) {
  if (a === b) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

function getSortIcon(direction) {
  if (!direction) return <FaSort />;
  if (direction === "asc") return <FaSortUp />;
  return <FaSortDown />;
}

export default function RecentArticles({ articles, loading, onArticleAction }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (articles || [])
      .filter((a) => (status === "all" ? true : a.status === status))
      .filter((a) => {
        if (!q) return true;
        return (
          a.title.toLowerCase().includes(q) ||
          a.author.toLowerCase().includes(q) ||
          String(a.views).includes(q) ||
          String(a.date).includes(q)
        );
      });
  }, [articles, query, status]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((x, y) => {
      const v = compare(x[sortKey], y[sortKey]);
      return sortDir === "asc" ? v : -v;
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "title" || key === "author" ? "asc" : "desc");
  };

  const onExport = () => {
    const rows = sorted.map((a) => ({
      Title: a.title,
      Author: a.author,
      Status: a.status,
      Views: a.views,
      Date: a.date,
    }));
    const csv = [
      Object.keys(rows[0] || { Title: "", Author: "", Status: "", Views: "", Date: "" }).join(","),
      ...rows.map((r) => [r.Title, r.Author, r.Status, r.Views, r.Date].map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recent-events.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="adminCard">
      <div className="adminCardHeader">
        <h3 className="adminCardTitle">Recent events</h3>
        <button type="button" className="adminButton" onClick={onExport} disabled={loading || sorted.length === 0}>
          <span style={{ marginRight: 8, display: "inline-flex" }} aria-hidden="true">
            <FaDownload />
          </span>
          Export
        </button>
      </div>

      <div className="adminToolbar">
        <div className="adminSearch">
          <span className="adminSearchIcon" aria-hidden="true">
            <FaSearch />
          </span>
          <input
            className="adminSearchInput"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events, authors, dates..."
            aria-label="Search events"
          />
        </div>
        <div className="adminToolbarRight">
          <select className="adminSelect" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter status">
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      <div className="adminTableWrap">
        <table className="adminTable">
          <thead>
            <tr>
              <th>
                <button type="button" className="adminThButton" onClick={() => toggleSort("title")}>
                  Title {getSortIcon(sortKey === "title" ? sortDir : null)}
                </button>
              </th>
              <th>
                <button type="button" className="adminThButton" onClick={() => toggleSort("author")}>
                  Author {getSortIcon(sortKey === "author" ? sortDir : null)}
                </button>
              </th>
              <th>
                <button type="button" className="adminThButton" onClick={() => toggleSort("status")}>
                  Status {getSortIcon(sortKey === "status" ? sortDir : null)}
                </button>
              </th>
              <th>
                <button type="button" className="adminThButton" onClick={() => toggleSort("views")}>
                  Views {getSortIcon(sortKey === "views" ? sortDir : null)}
                </button>
              </th>
              <th>
                <button type="button" className="adminThButton" onClick={() => toggleSort("date")}>
                  Date {getSortIcon(sortKey === "date" ? sortDir : null)}
                </button>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[0, 1, 2].map((i) => (
                  <tr key={i} aria-hidden="true">
                    <td colSpan={6} style={{ padding: 0 }}>
                      <div className="adminSkeletonRow">
                        <div className="adminSkeleton" />
                        <div className="adminSkeleton" />
                        <div className="adminSkeleton" />
                        <div className="adminSkeleton" />
                        <div className="adminSkeleton" />
                        <div className="adminSkeleton" />
                      </div>
                    </td>
                  </tr>
                ))}
              </>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="adminEmpty">No events found.</div>
                </td>
              </tr>
            ) : (
              sorted.map((a) => (
                <ArticleRow
                  key={a.id}
                  article={a}
                  onAction={(type, article) => {
                    onArticleAction?.(type, article);
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

